"use client";

import { Lock } from "lucide-react";

import { useLockImpression } from "@/lib/analytics/useLockImpression";

import { useAuthModal } from "../hooks/useAuthModal";
import { useUser } from "../hooks/useUser";

type LockedOverlayProps = {
  message?: string;
  compact?: boolean;
  /**
   * Replaces the default "Sign in" button — an upgrade lock passes its own
   * ("See Pro plans" → the pricing modal). Without it the lock is a sign-in
   * wall, as before.
   */
  action?: { label: string; onClick: () => void };
  /** Analytics surface name; when set, a "lock viewed" event fires once seen. */
  surface?: string;
  /** What unlocks it — defaults to an account for a sign-in wall, else "pro". */
  required?: "account" | "pro" | "studio";
};

export function LockedOverlay({
  message = "Sign in to unlock",
  compact,
  action,
  surface,
  required,
}: LockedOverlayProps) {
  const { open } = useAuthModal();
  const { user, plan, loading } = useUser();
  const need = required ?? (action ? "pro" : "account");
  // Sign-in walls count only for signed-out viewers; upgrade locks only once
  // the plan has resolved (a signed-in user reads "anonymous" for a moment).
  const settled = !loading && !(user && plan === "anonymous");
  const impressionRef = useLockImpression(surface, {
    required: need,
    plan,
    enabled: need === "account" ? !loading && !user : settled,
  });

  return (
    <div
      ref={impressionRef}
      className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-2.5"
    >
      <div className="flex items-center gap-2 px-4 text-center text-[#c7d5e0]">
        <Lock className={compact ? "w-4 h-4 shrink-0" : "w-5 h-5 shrink-0"} />
        <span className={`font-medium ${compact ? "text-xs" : "text-sm"}`}>{message}</span>
      </div>
      <button
        type="button"
        onClick={action ? action.onClick : () => open()}
        className={[
          "rounded-xl bg-[#4c9cd5] font-semibold text-[#0a0f15] hover:bg-[#5aa9de] active:bg-[#3d8ac2] transition-colors",
          compact ? "px-4 py-1.5 text-xs" : "px-5 py-2 text-sm",
        ].join(" ")}
      >
        {action ? action.label : "Sign in"}
      </button>
    </div>
  );
}
